import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  ReactiveJsonRoot as ReactiveJsonRootImport,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'
import yaml from 'js-yaml'
import { expandEditable } from '../editable/expandEditable'
import { BuilderContext } from '../BuilderContextInstance'
import { unwrapDecoupled } from '../editable/decoupledEnvelope'
import { unitEndpoint } from '../editable/unitEndpoint'

/**
 * UnitMount — the universal composition primitive (ADR-001).
 *
 * Mounts one editable unit inside another from two independently-addressable
 * references: a TEMPLATE (the rjBuild) and a DATA source (the Syncable apiData).
 * Sharing is just two units pointing at the same resource — same template ref =
 * shared appearance (def/instance), same data ref = shared content (fragment).
 *
 * Sources (for `template:`/`data:`, or the fused `nid:`/`url:`/`build:` shorthands):
 *   - an entity reference  → `{nid}` (a `builder_unit`, permissioned by Drupal).
 *     Son gabarit vient de son build inline, ou à défaut de sa définition de
 *     catalogue (clé `type`) — c'est ce qui permet de monter par référence un
 *     bloc ordinaire, donc de PARTAGER son contenu entre plusieurs pages.
 *   - a public file/URL    → `{url}` (a reactive-json YAML template, outside the builder)
 *   - an already-parsed build → `{build}` (inline, template only)
 *
 * It collapses the ~12-line `additionalDataSource` + `parse-fragment-build`
 * dataProcessor + `ReactiveJsonSubroot` pattern into one element, and unifies the
 * formerly-divergent load paths: an entity Syncable Object and a raw YAML file
 * BOTH go through one fetch → `yaml.load` → `expandEditable` → subroot pipeline.
 * (The lib's native `rjBuildUrl` fetches raw text and never runs `expandEditable`,
 * so URL-loaded templates could not carry inline `editable:` — UnitMount closes
 * that gap.)
 *
 * The mounted build is its own editable unit: its root is a `BuilderUnit` +
 * `DataSync` that persists to the unit's OWN `submission_url`. The template is
 * public; the data it loads is permission-controlled by Drupal. A file/build
 * template (or no data source) has no Syncable → no `submission_url` → its
 * content is read-only (presentational).
 *
 * Stable mount: build + dataOverride are resolved ONCE per source identity and
 * kept by reference, so a parent re-render never re-seeds the subroot store
 * (which would wipe pending DataSync edits) and the inner unit's `item_url`
 * never transiently flickers to undefined.
 *
 * Recursion guard: the chain of unit identities mounted above this point is
 * carried in context; a unit that (transitively) embeds itself renders a
 * stop-notice instead of looping.
 *
 * `FragmentEmbed` is a retro-compat alias (see bottom of file).
 *
 * YAML usage:
 *   - type: UnitMount
 *     nid: ~~.fragmentRef.nid          # fused: template AND data from one entity
 *   - type: UnitMount
 *     template: { url: /content/builder/fragments/cta.yaml }  # shared template…
 *     data: { nid: ~~.row.nid }                                # …per-place data
 */

// The unit identities currently mounted above this point (outermost-first).
// Exported so BuilderUnit can tell it's inside a UnitMount subroot (→ strict
// editability gate: read-only without a submission_url, no new-block leniency).
export const UnitChainContext = createContext([])
const MAX_DEPTH = 8

export const UnitMount = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const chain = useContext(UnitChainContext)

  // Registre du catalogue. Une unité montée par référence n'a pas forcément de
  // build inline : quand elle passe par une définition (`field_unit_template`,
  // servi sous la clé `type`), son gabarit est celui du catalogue — le MÊME
  // rjBuild que si le bloc était posé en place, donc une évolution du gabarit
  // continue de se propager. Lu par ref pour ne pas relancer la résolution à
  // chaque rechargement du registre. Hors builder, le contexte est absent.
  const builder = useContext(BuilderContext)
  const registryByType = useMemo(() => {
    const map = {}
    for (const entry of builder?.registry ?? []) if (entry.type) map[entry.type] = entry
    return map
  }, [builder?.registry])
  const registryRef = useRef(registryByType)
  registryRef.current = registryByType

  const resolve = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  // Resolve a source descriptor into `{kind, ...}`. Accepts a scalar (→ nid), or
  // an object `{nid}|{ref}|{url}|{build}`. Returns null when nothing resolves.
  const normSource = (src) => {
    if (src == null) return null
    if (typeof src === 'object' && !Array.isArray(src)) {
      if (src.build != null) return { kind: 'build', build: src.build }
      const n = resolve(src.nid ?? src.ref ?? null)
      if (n != null && n !== '') return { kind: 'nid', nid: n }
      const u = resolve(src.url ?? null)
      if (u) return { kind: 'url', url: u }
      return null
    }
    const n = resolve(src)
    return n != null && n !== '' ? { kind: 'nid', nid: n } : null
  }

  // Two independently-addressable references (ADR-001): the TEMPLATE (rjBuild)
  // and the DATA (Syncable apiData). Sharing is just two units pointing at the
  // same resource — same template ref = shared appearance, same data ref =
  // shared content. The `nid:`/`url:`/`build:` shorthands are the FUSED case
  // (template AND data from one source — the original FragmentEmbed behavior).
  const decoupled = props.template != null || props.data != null
  let tSrc, dSrc
  if (decoupled) {
    tSrc = normSource(props.template)
    dSrc = props.data != null ? normSource(props.data) : null
  } else {
    let fused = null
    if (props.build != null) fused = { kind: 'build', build: props.build }
    else if (props.nid != null || props.ref != null) fused = normSource(props.nid ?? props.ref)
    else if (props.url != null) {
      const u = resolve(props.url)
      fused = u ? { kind: 'url', url: u } : null
    }
    tSrc = fused
    dSrc = fused
  }

  const keyOf = (s) =>
    !s ? 'none' : s.kind === 'nid' ? `nid:${s.nid}` : s.kind === 'url' ? `url:${s.url}` : 'build'
  const identity = `t:${keyOf(tSrc)}|d:${keyOf(dSrc)}`

  // Resolved-once state. `null` while loading; `{ build, dataOverride }` once
  // ready; `{ error }` on failure. Kept stable across re-renders (no re-seed).
  const [resolved, setResolved] = useState(null)
  const identityRef = useRef(identity)

  useEffect(() => {
    if (!tSrc) {
      setResolved({ error: 'no-source' })
      return
    }
    // Recursion / depth stop: don't even fetch if we'd loop.
    if (chain.includes(identity) || chain.length >= MAX_DEPTH) {
      setResolved({ error: 'recursion' })
      return
    }

    let cancelled = false
    identityRef.current = identity
    setResolved(null)

    const finish = (next) => {
      if (!cancelled && identityRef.current === identity) setResolved(next)
    }

    const parse = (text) => {
      const parsed = typeof text === 'string' ? yaml.load(text) : text
      if (!parsed || typeof parsed !== 'object') throw new Error('empty/invalid build')
      return expandEditable(parsed)
    }

    // Fetch a builder_fragment syncable once per nid (dedupes the common fused
    // case where template and data are the same entity → a single request).
    const fragCache = {}
    const fetchFrag = (nid) => {
      if (!fragCache[nid]) {
        // `credentials` est load-bearing : sans cookie de session, le rendu
        // decoupled n'émet aucune URL d'écriture — l'unité montée serait
        // toujours en lecture seule, même pour qui a le droit de l'éditer.
        fragCache[nid] = fetch(unitEndpoint(nid), {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        })
          .then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`)
            return r.json()
          })
      }
      return fragCache[nid]
    }

    // Le gabarit d'une unité : son build inline (axe 2) ou, à défaut, celui de
    // sa définition de catalogue (axe 1). `normalizeRegistryItem` a déjà passé
    // ce rjBuild par `expandEditable`.
    const buildFromUnit = (unit) => {
      const d = unwrapDecoupled(unit)
      if (d.build) return parse(d.build)
      const def = d.type ? registryRef.current[d.type] : null
      if (!def?.rjBuild) throw new Error('unknown def type: ' + String(d.type))
      return def.rjBuild
    }

    // La PAGE d'accueil du montage, comme `BuilderBlockMount` la pose pour un
    // bloc ordinaire (`~~._page_`). Une unité montée PAR RÉFÉRENCE ne la
    // recevait pas : `dataOverride` remplace le `data:` du rjBuild, et rien ici
    // ne la remettait. Un en-tête partagé ne pouvait donc pas savoir quelle page
    // est affichée (entrée de menu courante), et un formulaire posé dans un pied
    // de page partagé envoyait un `pageUrl` vide — sans erreur, des deux côtés.
    // Aucun YAML ne sait lire `location` : c'est le geste irréductible.
    // Figée au montage, et c'est juste : les liens du builder sont des <a>
    // ordinaires, une navigation recharge la page et remonte l'unité.
    const pageFacts = () => ({
      url: typeof window === 'undefined' ? '' : window.location.href,
      path: typeof window === 'undefined' ? '' : window.location.pathname,
      host: typeof window === 'undefined' ? '' : window.location.host,
    })

    // Même fusion que BuilderBlockMount : `dataOverride` remplace le `data:` du
    // rjBuild en bloc, or certains gabarits y gardent des valeurs structurelles
    // (listes de navigation, liens sociaux) absentes de la donnée de l'unité.
    // Sans ce merge, elles deviennent inatteignables une fois montées ici.
    const withSeed = (build, override) => {
      if (!override) return override
      const unitData = override.apiData?.data?.objectData?.data
      if (!unitData || typeof unitData !== 'object' || Array.isArray(unitData)) {
        return { ...override, _page_: pageFacts() }
      }
      return { ...(build?.data ?? {}), ...unitData, ...override, _page_: pageFacts() }
    }

    const apiDataFromSyncable = (s) => {
      const d = unwrapDecoupled(s)
      return {
        apiData: {
          // `item_url` est servi dans les deux formes ; les URLs d'écriture, non
          // — c'est précisément leur absence qui rend l'unité non éditable
          // (BuilderUnit ne se fie qu'à `submission_url`, aucun système de
          // permissions parallèle).
          item_url: s.item_url ?? d.item_url ?? null,
          submission_url: s.submission_url ?? null,
          delete_url: s.delete_url ?? null,
          // builder_unit emits its data under `objectData` (unified decoupled key).
          data: { objectData: d.objectData ?? { data: {} } },
        },
      }
    }

    const loadBuild = async () => {
      if (tSrc.kind === 'build') return parse(tSrc.build)
      if (tSrc.kind === 'url') {
        const r = await fetch(tSrc.url, { headers: { Accept: 'text/yaml, text/plain, */*' } })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return parse(await r.text())
      }
      return buildFromUnit(await fetchFrag(tSrc.nid)) // nid
    }

    const loadDataOverride = async () => {
      // Only an entity data source carries a Syncable (apiData). A file/build
      // template or no data source → presentational (read-only), no apiData.
      if (!dSrc || dSrc.kind !== 'nid') return undefined
      return apiDataFromSyncable(await fetchFrag(dSrc.nid))
    }

    Promise.all([loadBuild(), loadDataOverride()])
      .then(([build, dataOverride]) => finish({ build, dataOverride: withSeed(build, dataOverride) }))
      .catch((err) => {
        console.error('UnitMount: failed to resolve', identity, err)
        finish({ error: 'fetch' })
      })
    return () => { cancelled = true }
  }, [identity])

  // The subroot is mounted via the parent's ReactiveJsonRoot so it inherits the
  // exact same plugins (BuilderUnit, DataSync, …). Memoize the chain we pass
  // down so the context value identity is stable.
  const childChain = useMemo(() => [...chain, identity], [chain, identity])

  const ReactiveJsonRoot = globalDataContext?.ReactiveJsonRoot ?? ReactiveJsonRootImport
  const plugins = globalDataContext?.plugins ?? {}

  if (resolved?.error === 'recursion') {
    return (
      <div className="builder-edit-only rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-700">
        ⟳ Fragment imbriqué récursif ignoré ({identity}).
      </div>
    )
  }
  if (resolved?.error === 'no-source') {
    // No fragment chosen yet (e.g. an empty Fragment block whose fragmentRef is
    // null). Not an error — a discreet edit-only hint, nothing in view mode.
    return (
      <div
        className="builder-edit-only flex min-h-[160px] w-full items-center justify-center rounded border border-dashed border-amber-300 px-3 py-2 text-center text-xs font-medium text-amber-800"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, rgba(245,158,11,0.16) 0 14px, rgba(245,158,11,0.04) 14px 28px)',
        }}
      >
        🚧 Aucun fragment sélectionné.
      </div>
    )
  }
  if (resolved?.error) {
    return (
      <div className="builder-edit-only rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700">
        Fragment introuvable ({identity}).
      </div>
    )
  }
  if (!resolved?.build) {
    // Skeleton while loading — keeps layout from jumping.
    return <div className="min-h-[44px] animate-pulse rounded bg-gray-100" aria-busy="true" />
  }

  return (
    <UnitChainContext.Provider value={childChain}>
      <ReactiveJsonRoot
        maybeRawAppRjBuild={resolved.build}
        dataOverride={resolved.dataOverride}
        plugins={plugins}
      />
    </UnitChainContext.Provider>
  )
}

/**
 * Retro-compat alias: `FragmentEmbed` was the original (fused-source) name of
 * this primitive. Existing builds (e.g. the Fragment block) still use it; new
 * builds should use `UnitMount`.
 */
export const FragmentEmbed = UnitMount
